import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import EssayCard from "@/components/EssayCard";
import { LinkedCircles, DotGrid, CircleOutline, HairlineCross } from "@/components/Shapes";

// Section intros — verbatim from Leah Moschella brand PDF
const INTROS = {
    "overachieving-for-me": {
        title: "Overachieving for Me",
        intro: "These essays are the mistakes I've made in pursuit of getting everything right.",
        body: [
            "I used to consider myself the \"Career-driven Superwoman.\" A childless woman whose only boss was her Google Calendar. Frankly, it made me annoying to be around, not the enviable badass I envisioned.",
            "In this collection of stories, please don't expect tips on productivity or time blocking to reach your KPIs. Don't expect career advice or a saccharine inspiration to take a leap and follow your dreams without a parachute.",
            "Do expect dumb things I've done by taking the wrong risks, committing to invisible labor that gave me nothing, being afraid to ask, or taking my anger out on senators by sticking gum on their leather couch.",
            "Turns out perfection is an exhausting hobby.",
        ],
        image:
            "https://customer-assets.emergentagent.com/job_couch-conversations/artifacts/fb9brfn1_PXL_20250211_122635364.PORTRAIT~3.webp",
        image_alt: "Leah, mustard blazer and red lipstick — the corporate uniform for a recovering overachiever.",
        shape: "orange",
        image_aspect: "aspect-[4/5]",
    },
    "champagne-and-bagel-bites": {
        title: "Champagne and Bagel Bites",
        intro:
            "Stories about growing up with less, finding myself with more, and the wonderfully uncomfortable whiplash of moving between worlds.",
        body: [
            "Will I ever let go of the poverty mindset? Does the kid who qualified for free lunch ever stop showing up, and how do I kick that distracting kid out of my three-star Michelin tasting menu?",
            "In this collection of stories you can expect awkward moments aboard former presidents' yachts, guilt over achieving the life I spent years dreaming about, and unwavering love for the community that raised me. Expect stories about class, belonging, ambition, and the strange realization that crossing society's lines doesn't make them invisible.",
            "Somewhere between prison visits and policy meetings, I realized that arriving at the life I'd always imagined didn't answer as many questions as it created. Turns out billionaires like frozen Bagel Bites, too.",
        ],
        image:
            "https://customer-assets.emergentagent.com/job_couch-conversations/artifacts/53tpvcn7_Screenshot_20260117-085948.png",
        image_alt: "Leah at afternoon tea, two champagne flutes in front of her — the Bagel-Bites-eating kid, all grown up.",
        shape: "gold",
    },
    "life-as-an-elective": {
        title: "Life as an Elective",
        intro:
            "Stories about what happens when you actually close the laptop at 5, pay your bills, and make time for the adventures that make you smile.",
        body: [
            "Will I ever perform on a Broadway stage or know the feeling of scoring a championship-winning goal? Probably not. I'm in my 40s and just thinking about those activities drenches me in sweat. But aging knees and heartburn hasn't stopped me from treating my free time like it's worth investing in. Turns out you don't have to be the one under the spotlight to be changed by the show.",
            "This collection includes my travel mishaps like being tricked into eating North African cow balls, ambitious attempts to learn tap dance in my forties, and irrational loyalty to women's sports teams. Sometimes it's the simple act of signing out of the Zoom account that shapes us the most.",
            "Somewhere in between beer-soaked stadiums, and being the jerk who talks to her airplane seat neighbor on a 7am business flight, I embraced the reality that joy isn't a distraction from real life. We actually can choose our communities and don't have to rely only on colleagues as our friends. It's the passions we pursue that give us the moments of collective wonder and the reminder that we're not in this chaotic world figuring it out alone.",
        ],
        image:
            "https://customer-assets.emergentagent.com/job_couch-conversations/artifacts/cccqdat3_PXL_20251226_130822928.MP.webp",
        image_alt: "Leah in front of Iceland's Goðafoss waterfall, arms wide open — this is what closing the laptop looks like.",
        shape: "teal",
    },
};

export default function SectionPage({ slug }) {
    const params = useParams();
    const category = slug || params.category;
    const [essays, setEssays] = useState(null);
    const intro = INTROS[category];

    useEffect(() => {
        setEssays(null);
        api.get("/essays", { params: { category } })
            .then((r) => setEssays(r.data))
            .catch(() => setEssays([]));
    }, [category]);

    if (!intro) {
        return (
            <div className="pt-40 text-center px-6" data-testid="section-not-found">
                <p className="text-2xl italic text-[rgb(var(--teal))]">Section not found.</p>
                <Link to="/" className="btn-ghost mt-6 inline-flex">← Back home</Link>
            </div>
        );
    }

    return (
        <div className="pt-28 md:pt-32 pb-16" data-testid={`section-page-${category}`}>
            {/* Section hero */}
            <section className="mx-auto max-w-[1280px] px-6 md:px-10 pt-8 pb-10 relative overflow-hidden">
                <div className="grid lg:grid-cols-12 gap-10 items-end">
                    <div className="lg:col-span-7 relative">
                        <LinkedCircles size={64} color={intro.shape} stroke={1.5} className="mb-4" />
                        <p className="overline">The Collection</p>
                        <h1 className="mt-4 text-3xl md:text-5xl lg:text-[52px] font-bold text-[rgb(var(--teal))] tracking-tight leading-[1.05]">
                            {intro.title}
                        </h1>
                        <p className="mt-6 text-lg md:text-xl italic text-[rgb(var(--orange))] max-w-2xl leading-snug">
                            {intro.intro}
                        </p>
                    </div>
                    <div className="lg:col-span-5 relative">
                        <div className="absolute -bottom-4 -right-4 hidden md:block z-0">
                            <DotGrid rows={4} cols={4} gap={12} size={2.5} color="teal" />
                        </div>
                        <motion.div
                            initial={{ opacity: 0, scale: 1.03 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                            className={`frame ${intro.image_aspect || "aspect-[5/4]"} relative z-10 max-w-[440px] ml-auto`}
                        >
                            <img
                                src={intro.image}
                                alt={intro.image_alt}
                            />
                        </motion.div>
                    </div>
                </div>
            </section>

            <div className="mx-auto max-w-[1280px] px-6 md:px-10">
                <div className="rule" />
            </div>

            {/* Intro body — verbatim intro-language from PDF (burnt orange box for emphasis) */}
            <section className="mx-auto max-w-[1280px] px-6 md:px-10 py-12 md:py-16">
                <div className="bg-[rgb(var(--orange))] text-[rgb(var(--paper))] relative overflow-hidden">
                    <div className="absolute top-6 right-6 opacity-50 hidden md:block">
                        <LinkedCircles size={80} color="gold" stroke={1.5} />
                    </div>
                    <div className="p-8 md:p-12 lg:p-16 grid md:grid-cols-12 gap-8 lg:gap-10 relative z-10">
                        <div className="md:col-span-4">
                            <div className="flex items-center gap-3">
                                <HairlineCross size={18} color="gold" />
                                <p className="overline text-[rgb(var(--gold))]">Introduction</p>
                            </div>
                            <div className="mt-3 h-[2px] w-10 bg-[rgb(var(--paper))]" />
                        </div>
                        <div className="md:col-span-8 space-y-5 text-[17px] md:text-[18px] leading-relaxed text-[rgb(var(--paper))] font-medium max-w-2xl">
                            {intro.body.map((p, i) => (
                                <p key={i}>{p}</p>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            <div className="mx-auto max-w-[1280px] px-6 md:px-10">
                <div className="rule" />
            </div>

            {/* Essays list */}
            <section className="mx-auto max-w-[1280px] px-6 md:px-10 py-12 md:py-16">
                <div className="flex items-end justify-between gap-4 mb-8">
                    <div>
                        <p className="overline">Essays</p>
                        <h2 className="mt-2 h-section">From this collection.</h2>
                    </div>
                </div>

                {essays === null && (
                    <p className="py-16 italic text-[rgb(var(--ink-2))]">Loading essays…</p>
                )}
                {essays && essays.length === 0 && (
                    <div className="border hairline p-10 md:p-16 text-center relative bg-[rgb(var(--paper-2))]">
                        <div className="absolute top-4 right-4">
                            <CircleOutline size={40} color="gold" stroke={1.25} />
                        </div>
                        <p className="overline text-[rgb(var(--gold))]">Coming Soon</p>
                        <p className="mt-4 text-2xl md:text-3xl font-bold text-[rgb(var(--teal))]">
                            The first essays are on their way.
                        </p>
                        <p className="mt-3 text-[15px] text-[rgb(var(--ink-2))] max-w-md mx-auto">
                            Subscribe below and I&apos;ll send the first one the moment it&apos;s ready.
                        </p>
                    </div>
                )}
                {essays && essays.length > 0 && (
                    <div className="border-t hairline">
                        {essays.map((e) => (
                            <EssayCard key={e.id} essay={e} variant="row" />
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
